// Announcements — list agency announcements with status chips, a composer that
// creates a draft (title, body, audience), and a Publish button that promotes a
// draft to published. Backed by GET/POST /announcements + PATCH /announcements/:id.
import { useEffect, useState } from 'react';
import type { Announcement } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, Chip, EmptyNote, ErrorNote, SectionHeader, Select, Spinner } from './ui';
import { formatApiTime } from '../../lib/time';

const DASH = '—';

const AUDIENCE_LABEL: Record<Announcement['audience'], string> = {
  clients: 'Clients',
  team: 'Team',
  all: 'Everyone',
};
const SOURCE_LABEL: Record<Announcement['source'], string> = {
  manual: 'Manual',
  platform_changelog: 'Platform changelog',
  entity_change: 'Entity change',
};

// Published/created stamp. `Date.parse` here read zone-less D1 values as local — see lib/time.ts.
const fmtWhen = (iso: string | null) => formatApiTime(iso, DASH);

export default function Announcements() {
  const [items, setItems] = useState<Announcement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    setError(null);
    agencyApi
      .announcements()
      .then(setItems)
      .catch((e: Error) => setError(e.message));
  }
  useEffect(load, []);

  async function publish(id: string) {
    setBusyId(id);
    try {
      await agencyApi.publishAnnouncement(id);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
      {/* List */}
      <div className="space-y-4">
        {error && <ErrorNote error={error} />}
        {!items && !error && <Spinner label="Loading announcements…" />}
        {items && items.length === 0 && (
          <EmptyNote>No announcements yet. Compose one to keep clients and your team in the loop.</EmptyNote>
        )}
        {items && items.length > 0 && (
          <div className="space-y-3">
            {items.map((a) => (
              <Card key={a.id} className="p-5">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {a.status === 'published' ? (
                    <Chip label="Published" color="#16a34a" />
                  ) : (
                    <Chip label="Draft" color="#d97706" />
                  )}
                  <Chip label={AUDIENCE_LABEL[a.audience]} muted />
                  {a.source !== 'manual' && <Chip label={SOURCE_LABEL[a.source]} muted />}
                  <span className="ml-auto text-xs text-slate-400">
                    {a.status === 'published' ? `Published ${fmtWhen(a.published_at)}` : `Created ${fmtWhen(a.created_at)}`}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-slate-900">{a.title}</h3>
                {a.body && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{a.body}</p>}
                {a.status === 'draft' && (
                  <div className="mt-3">
                    <Button onClick={() => publish(a.id)} disabled={busyId === a.id}>
                      {busyId === a.id ? 'Publishing…' : 'Publish'}
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <Composer onCreated={load} />
    </div>
  );
}

// ── Composer ───────────────────────────────────────────────────────
function Composer({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<Announcement['audience']>('all');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await agencyApi.createAnnouncement({
        title: title.trim(),
        body: body.trim() || null,
        audience,
      });
      setTitle('');
      setBody('');
      setAudience('all');
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="h-fit p-5">
      <SectionHeader title="New announcement" subtitle="Saved as a draft — publish when ready." />
      {error && <div className="mb-3 text-xs text-rose-600">{error}</div>}
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What's new…"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="Details (optional)…"
            className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Audience</label>
          <Select value={audience} onChange={(v) => setAudience(v as Announcement['audience'])} className="w-full">
            <option value="all">Everyone</option>
            <option value="clients">Clients</option>
            <option value="team">Team</option>
          </Select>
        </div>
        <Button onClick={submit} disabled={saving || !title.trim()} className="w-full">
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
      </div>
    </Card>
  );
}
